import type { ImageGenerateParams } from '../base';
import { mapResolutionToSize } from '../../shared/image-size';
import type { SegmindModelConfig, TextSize, Resolution, InputPayloadType } from './types';
import { segmindModelConfigs } from './config';
import { translateToEnglish } from '../../shared/translate';
import { needsTranslation } from '../tools';

/**
 * 获取模型配置
 */
export function getSegmindModelConfig(modelId: string): SegmindModelConfig {
    const config = segmindModelConfigs[modelId];
    if (!config) {
        throw new Error(`Model ${modelId} not found`);
    }
    return config;
}

/**
 * 构建输入参数
 */
export async function buildInputPayloadFromConfig(
    modelId: string,
    params: ImageGenerateParams
): Promise<InputPayloadType> {
    // 获取服务商配置
    const config = getSegmindModelConfig(modelId);
    
    // 从 Provider Options 中获取 image_input
    const image_urls: string[] = (params.providerOptions as any)?.url?.image_input ?? [];

    // 分辨率
    const image_resolution: Resolution = '4K';

    // 比例: 需要类型转换
    const pixelSize = params.size === 'match' ? undefined : (params.size as `${number}x${number}`);
    const image_size: TextSize = mapResolutionToSize(pixelSize, 'text');

    // 提示词翻译
    let prompt = params.prompt;
    if (config.enableTranslation && needsTranslation(prompt)) {
        try {
            prompt = await translateToEnglish(prompt);
        } catch (error) {
            // 翻译失败时使用原始提示词
            console.error(`[SEGMIND][${modelId}] Failed to translate prompt:`, error);
        }
    }

    // 获取生成图片数量
    // 优先从 providerOptions._imagesCount 获取（当 useRequestBodyForCount 为 true 时）
    // 否则从 params.n 获取（当 useRequestBodyForCount 为 false 时，AI SDK 会使用这个值）
    const imagesCount = (params.providerOptions as any)?._imagesCount ?? params.n;

    // 构建输入参数
    const result = await config.buildInput({
        prompt: prompt,
        image_urls: image_urls,
        size: params.size,
        aspectRatio: params.aspectRatio,
        seed: params.seed,
        image_size: image_size,
        image_resolution: image_resolution,
        providerOptions: params.providerOptions,
        n: imagesCount, // 传递生成图片数量（用于请求体）
    });

    return result;
}